import { useTheme } from '../theme/store';
import { NavButton } from './MailShell';
import { usePanes } from '../ui/viewport';

/*
    The two-column geometry every screen inside the shell uses.

    Deliberately the same shape as the mail screen — a narrow column where the
    message list sits, a wide one where the reading pane sits — and deliberately
    the same width, taken from the same setting the mail list is resized by. Move
    between Mail, Contacts, the Directory and Settings and the columns don't
    jump: only what is in them changes.
*/

export function Pane({
    title,
    count,
    actions,
    longList,
    children,
}: {
    title: string;
    /** The `// 47 CONTACTS` line under the title. */
    count?: string;
    actions?: React.ReactNode;
    /**
     * What is inside is a list of people, not a handful of tabs.
     *
     * Settings has five tabs and they make a decent strip along the top of a narrow window. An
     * address book has as many rows as somebody has friends, and a strip of four hundred names
     * scrolling sideways is not a list anybody can read. So this one keeps its column and takes
     * the screen instead — which is what the mail list does at the same width.
     */
    longList?: boolean;
    children: React.ReactNode;
}) {
    const listWidth = useTheme((s) => s.listWidth);
    const { onePane } = usePanes();

    /*
        Too narrow for two columns and this stops being one. The list of things
        to choose from becomes a strip along the top and the detail gets the rest
        of the screen — the same rearrangement the mail screen makes, so the two
        do not disagree about what a narrow window looks like.
    */
    const strip = onePane && !longList;
    return (
        <section
            style={{
                display: 'flex',
                minWidth: 0,
                background: 'var(--bg)',
                ...(onePane
                    ? {
                          width: '100%',
                          flexDirection: 'column',
                          ...(longList
                              ? { flex: 1, minHeight: 0 }
                              : { flex: '0 0 auto', borderBottom: '1px solid var(--border)' }),
                      }
                    : {
                          width: Math.min(Math.max(listWidth, 280), 520),
                          flex: '0 0 auto',
                          flexDirection: 'column',
                          borderRight: '1px solid var(--border)',
                      }),
            }}
        >
            <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid var(--border)', display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <NavButton />
                    <div data-testid="pane-title" style={{ fontSize: 'var(--fs-lg)', fontWeight: 600 }}>{title}</div>
                    {/*
                        Düz metin, mono kicker değil (arayüz v2, 5. adım). Mono artık yalnız
                        hizalamanın işe yaradığı yerlerde: sayı, tarih, boyut, sunucu yolu, kısayol.
                        "// 1 kişi" bunların hiçbiri değil — bir başlığın altındaki açıklama.
                    */}
                    {count ? (
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>{count}</div>
                    ) : null}
                </div>
                {/*
                    Its own row, and allowed to wrap.

                    A search box with `width: 100%` next to a button in one flex
                    row pushed the button off the edge — that is exactly how the
                    New contact button ended up overflowing.
                */}
                {actions ? <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{actions}</div> : null}
            </div>
            <div
                style={
                    strip
                        ? { display: 'flex', gap: 4, overflowX: 'auto', padding: '8px' }
                        : { flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 8px 16px' }
                }
            >
                {children}
            </div>
        </section>
    );
}

export function Detail({ children }: { children: React.ReactNode }) {
    return (
        <section style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', background: 'var(--surface)' }}>
            <div style={{ maxWidth: 780, margin: '0 auto', padding: '18px 22px 60px', display: 'grid', gap: 16 }}>{children}</div>
        </section>
    );
}

/** What a detail pane shows before anything is chosen. */
export function Empty({ title, hint }: { title: string; hint?: string }) {
    return (
        <section style={{ flex: 1, display: 'grid', placeItems: 'center', background: 'var(--surface)' }}>
            <div style={{ textAlign: 'center' }}>
                <div className="kicker">// {title}</div>
                {hint ? <div style={{ marginTop: 6, fontSize: 'var(--fs-md)', color: 'var(--text-mute)' }}>{hint}</div> : null}
            </div>
        </section>
    );
}

export const card: React.CSSProperties = {
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-card)',
    padding: 16,
    display: 'grid',
    gap: 14,
};

export const input: React.CSSProperties = {
    height: 30,
    // Grows and SHRINKS. `width: 100%` in a flex row is what pushes siblings out.
    flex: '1 1 140px',
    minWidth: 0,
    padding: '0 10px',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-btn)',
    outline: 'none',
    fontSize: 'var(--fs-sm)',
};

export const button = (primary = false): React.CSSProperties => ({
    height: 30,
    // Never the thing that gets squeezed: labels are longer in Turkish.
    flex: '0 0 auto',
    padding: '0 12px',
    background: primary ? 'var(--accent)' : 'transparent',
    color: primary ? 'var(--on-accent)' : 'var(--text-dim)',
    border: primary ? 'none' : '1px solid var(--border2)',
    borderRadius: 'var(--r-btn)',
    fontSize: 'var(--fs-sm)',
    fontWeight: primary ? 600 : 400,
    whiteSpace: 'nowrap',
});
